<!-- eslint-disable vue/multi-word-component-names -->
<template>
  <div class="space-y-6">
    <!-- 快捷入口 - 现代化卡片设计 -->
    <div class="bg-white rounded-2xl shadow-sm border border-slate-200/60 p-6">
      <div class="flex items-center justify-between mb-4">
        <h3 class="text-base font-semibold text-slate-900">快捷入口</h3>
        <span class="text-xs text-slate-400">常用操作快速访问</span>
      </div>
      <div class="grid grid-cols-2 gap-3 md:grid-cols-3 xl:grid-cols-6">
        <button
          v-for="item in visibleQuickActions"
          :key="item.label"
          class="group relative flex min-w-0 flex-col items-center gap-2.5 rounded-xl bg-slate-50 p-3.5 hover:-translate-y-0.5 hover:bg-primary-50 hover:shadow-md hover:shadow-primary-100/50 transition-all duration-300"
          @click="handleQuickAction(item)"
        >
          <div class="relative">
            <div :class="['flex h-10 w-10 items-center justify-center rounded-xl transition-all duration-300', item.iconBg]">
              <component :is="item.icon" class="h-4.5 w-4.5 transition-transform duration-300 group-hover:scale-110" />
            </div>
          </div>
          <span class="truncate text-center text-xs font-medium leading-5 text-slate-600 transition-colors group-hover:text-primary-700">{{ item.label }}</span>
        </button>
      </div>
    </div>

    <DashboardStatisticsSection v-if="showStatisticsSection" />

    <!-- 数据卡片区域 -->
    <div class="grid grid-cols-2 gap-6">
      <!-- 今日日程 -->
      <div :class="dashboardCardClass" :style="getDashboardCardOrderStyle('schedule')">
        <div class="flex items-center justify-between mb-4 shrink-0">
          <div class="flex items-center gap-2">
            <div class="w-1.5 h-6 bg-primary-500 rounded-full"></div>
            <h3 class="text-base font-semibold text-slate-900">今日日程</h3>
          </div>
          <router-link to="/schedule" class="group flex items-center gap-1 text-xs text-slate-400 hover:text-primary-600 transition-colors">
            查看全部
            <svg class="w-3.5 h-3.5 transition-transform group-hover:translate-x-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7"/></svg>
          </router-link>
        </div>
        <div :class="dashboardCardBodyClass">
          <div v-for="schedule in todaySchedules" :key="schedule.id" class="group relative flex items-center gap-3 p-3.5 rounded-xl cursor-pointer transition-all duration-200" :class="getScheduleBgClass(schedule)" @click="viewSchedule(schedule)">
            <div class="w-14 py-2 bg-white/60 rounded-lg text-center border border-slate-100/50 shrink-0">
              <p class="text-sm font-bold text-slate-800">{{ schedule.time }}</p>
            </div>
            <div class="relative">
              <div class="w-2.5 h-2.5 rounded-full" :class="getScheduleStatusDotClass(schedule)"></div>
            </div>
            <div class="flex-1 min-w-0">
              <p class="text-sm font-medium text-slate-800 truncate group-hover:text-primary-700 transition-colors">{{ schedule.title }}</p>
              <p class="text-xs text-slate-400 mt-0.5 flex items-center gap-1">
                <span>{{ schedule.time }}</span>
                <span v-if="schedule.endTime">- {{ schedule.endTime }}</span>
                <span v-if="schedule.location">· {{ schedule.location }}</span>
              </p>
            </div>
            <div class="flex items-center gap-2 shrink-0">
              <span v-if="schedule.status === 'completed'" class="px-2 py-0.5 bg-slate-100 text-slate-600 text-xs font-medium rounded-full">已结束</span>
              <span v-else-if="schedule.status === 'in_progress'" class="px-2 py-0.5 bg-blue-100 text-blue-700 text-xs font-medium rounded-full">进行中</span>
              <span v-else-if="schedule.status === 'pending'" class="px-2 py-0.5 bg-amber-100 text-amber-700 text-xs font-medium rounded-full">未开始</span>
              <button v-if="schedule.status === 'in_progress' && schedule.isOwner" @click.stop="completeSchedule(schedule)" class="px-2.5 py-1 text-xs font-medium text-primary-600 bg-primary-50 rounded-lg hover:bg-primary-100 transition-colors">完成</button>
              <div v-else-if="!schedule.isOwner && schedule.attendeeStatus === 1" class="flex items-center gap-1" @click.stop>
                <button @click="respondToInviteOnList(schedule, 2)" class="p-1.5 rounded-lg bg-emerald-100 hover:bg-emerald-200 text-emerald-700 transition-colors" title="接受"><svg class="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7"/></svg></button>
                <button @click="respondToInviteOnList(schedule, 3)" class="p-1.5 rounded-lg bg-red-100 hover:bg-red-200 text-red-700 transition-colors" title="拒绝"><svg class="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"/></svg></button>
              </div>
              <svg class="w-4 h-4 text-slate-300 group-hover:text-primary-400 transition-colors" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7"/></svg>
            </div>
          </div>
          <div v-if="todaySchedules.length === 0" class="flex flex-col items-center justify-center py-10">
            <div class="w-16 h-16 rounded-2xl bg-slate-100 flex items-center justify-center mb-3">
              <svg class="w-8 h-8 text-slate-300" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"/></svg>
            </div>
            <p class="text-sm text-slate-400">今日暂无日程</p>
            <router-link to="/schedule" class="mt-2 text-xs text-primary-600 hover:text-primary-700">去添加日程</router-link>
          </div>
        </div>
      </div>

      <!-- 今日计划 -->
      <div :class="dashboardCardClass" :style="getDashboardCardOrderStyle('todayPlan')">
        <div class="flex items-center justify-between mb-4 shrink-0">
          <div class="flex items-center gap-2">
            <div class="w-1.5 h-6 bg-primary-500 rounded-full"></div>
            <h3 class="text-base font-semibold text-slate-900">今日计划</h3>
            <span v-if="todayPlanPendingItems.length > 0" class="px-2 py-0.5 bg-emerald-100 text-emerald-700 text-xs font-medium rounded-full">{{ todayPlanPendingItems.length }}</span>
          </div>
          <div class="flex items-center gap-2">
            <button type="button" class="group flex items-center gap-1 text-xs text-slate-400 hover:text-primary-600 transition-colors" @click="goCreateTodayPlan">
              我的日报
              <svg class="w-3.5 h-3.5 transition-transform group-hover:translate-x-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7"/></svg>
            </button>
            <button v-if="canOpenTeamWorkReport" type="button" class="group flex items-center gap-1 text-xs text-slate-400 hover:text-primary-600 transition-colors" @click="goOpenTeamWorkReport">
              团队日报
              <svg class="w-3.5 h-3.5 transition-transform group-hover:translate-x-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7"/></svg>
            </button>
          </div>
        </div>

        <div :class="dashboardCardBodyClass">
        <div v-if="todayPlanLoading" class="flex flex-col items-center justify-center py-10">
          <div class="h-8 w-8 animate-spin rounded-full border-2 border-slate-200 border-t-primary-500"></div>
          <p class="mt-3 text-sm text-slate-400">正在加载今日计划...</p>
        </div>

        <div v-else-if="todayPlanSummarySubmitted" class="flex flex-col items-center justify-center py-10">
          <div class="w-16 h-16 rounded-2xl bg-emerald-100 flex items-center justify-center mb-3">
            <svg class="w-8 h-8 text-emerald-500" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z"/></svg>
          </div>
          <p class="text-sm font-medium text-slate-700">今日计划已完成并提交</p>
          <p class="mt-1 text-xs text-slate-400">今日日报总结已提交，无需继续处理。</p>
        </div>

        <div v-else-if="!todayPlanHasPlan" class="flex flex-col items-center justify-center py-10">
          <div class="w-16 h-16 rounded-2xl bg-emerald-100 flex items-center justify-center mb-3">
            <svg class="w-8 h-8 text-emerald-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2m-4 0a2 2 0 114 0m-4 0a2 2 0 104 0m-6 8h6m-6 4h6"/></svg>
          </div>
          <p class="text-sm text-slate-400">今日暂无工作计划</p>
          <button type="button" class="mt-2 text-xs text-primary-600 hover:text-primary-700 transition-colors" @click="goCreateTodayPlan">去创建计划</button>
        </div>

        <div v-else-if="todayPlanPendingItems.length > 0" class="space-y-2">
          <div
            v-for="item in todayPlanPendingItems"
            :key="todayPlanItemKey(item)"
            class="group relative flex items-start gap-3 p-3.5 rounded-xl cursor-pointer transition-all duration-200 hover:shadow-sm bg-slate-50 border border-slate-200"
            @click="openTodayPlanDialog(item)"
          >
            <div class="flex flex-col items-center gap-1 py-0.5 shrink-0">
              <div class="w-2.5 h-2.5 rounded-full bg-emerald-500"></div>
              <div class="w-0.5 h-4 rounded-full bg-emerald-200"></div>
            </div>
            <div class="min-w-0 flex-1">
              <div class="flex flex-wrap items-center gap-2">
                <p class="min-w-0 flex-1 truncate text-sm font-medium text-slate-800 group-hover:text-emerald-700 transition-colors">{{ todayPlanTitle(item) }}</p>
                <span class="shrink-0 px-2 py-0.5 bg-white/80 text-emerald-700 text-xs font-medium rounded-full">{{ todayPlanSourceLabel(item) }}</span>
                <span class="shrink-0 px-2 py-0.5 bg-white/80 text-slate-500 text-xs font-medium rounded-full">{{ todayPlanResultLabel(item.result) }}</span>
              </div>
              <p v-if="todayPlanDescription(item)" class="mt-0.5 line-clamp-2 text-xs text-slate-500">{{ todayPlanDescription(item) }}</p>
            </div>
            <button
              type="button"
              class="self-center shrink-0 rounded-lg bg-primary-600 px-3.5 py-1.5 text-xs font-medium text-white shadow-sm transition-all hover:bg-primary-700 hover:shadow-md"
              @click.stop="openTodayPlanDialog(item)"
            >
              处理
            </button>
          </div>
        </div>

        <div v-else class="flex flex-col items-center justify-center py-10">
          <div class="w-16 h-16 rounded-2xl bg-emerald-100 flex items-center justify-center mb-3">
            <svg class="w-8 h-8 text-emerald-500" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M9 12.75L11.25 15 15 9.75M21 12a9 9 0 11-18 0 9 9 0 0118 0z"/></svg>
          </div>
          <p class="text-sm font-medium text-slate-700">今日计划已全部处理</p>
          <button type="button" class="mt-2 text-xs text-primary-600 hover:text-primary-700 transition-colors" @click="goSubmitTodaySummary">去提交总结</button>
        </div>
        </div>
      </div>

      <!-- 待办事项 -->
      <div :class="dashboardCardClass" :style="getDashboardCardOrderStyle('todo')">
        <div class="flex items-center justify-between mb-4 shrink-0">
          <div class="flex items-center gap-2">
            <div class="w-1.5 h-6 bg-primary-500 rounded-full"></div>
            <h3 class="text-base font-semibold text-slate-900">待办事项</h3>

          </div>
          <router-link to="/todo" class="group flex items-center gap-1 text-xs text-slate-400 hover:text-primary-600 transition-colors">
            查看全部
            <svg class="w-3.5 h-3.5 transition-transform group-hover:translate-x-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7"/></svg>
          </router-link>
        </div>
        <div :class="dashboardCardBodyClass">
        <div v-if="dashboardTodos.length > 0" class="space-y-2">
          <div v-for="item in dashboardTodos" :key="item.id" class="group relative flex items-start gap-3 p-3.5 rounded-xl cursor-pointer transition-all duration-200 hover:shadow-sm" :class="item.overdue ? 'bg-red-50 border border-red-100' : 'bg-slate-50 border border-slate-200'" @click="openTodoDetail(item)">
            <div class="flex flex-col items-center gap-1 py-0.5 shrink-0">
              <div class="w-2.5 h-2.5 rounded-full" :class="item.overdue ? 'bg-red-500' : item.priority >= 3 ? 'bg-orange-500' : 'bg-amber-500'"></div>
              <div class="w-0.5 h-4 rounded-full" :class="item.overdue ? 'bg-red-200' : item.priority >= 3 ? 'bg-orange-200' : 'bg-amber-200'"></div>
            </div>
            <div class="flex-1 min-w-0">
              <div class="flex items-center gap-2">
                <p class="text-sm font-medium text-slate-800 truncate group-hover:text-primary-700 transition-colors">{{ item.title }}</p>
                <span v-if="item.overdue" class="px-1.5 py-0.5 bg-red-100 text-red-700 text-xs font-bold rounded-full shrink-0">逾期</span>
              </div>
              <p v-if="item.description" class="text-xs text-slate-400 mt-0.5 line-clamp-2">{{ item.description }}</p>
            </div>
            <button
              type="button"
              class="px-3.5 py-1.5 bg-primary-600 text-white text-xs font-medium rounded-lg hover:bg-primary-700 shadow-sm hover:shadow-md transition-all whitespace-nowrap self-center shrink-0"
              @click.stop="handleCompleteTodo(item)"
            >
              完成
            </button>
            <div class="flex flex-col items-end gap-1 shrink-0">
              <span class="px-2 py-0.5 text-xs font-medium rounded-full" :class="[item.overdue ? 'bg-red-100 text-red-700' : item.priority === 4 ? 'bg-red-100 text-red-700' : item.priority === 3 ? 'bg-orange-100 text-orange-700' : item.priority === 2 ? 'bg-amber-100 text-amber-700' : 'bg-slate-100 text-slate-600']">{{ item.priorityDesc || (item.priority === 1 ? '低' : item.priority === 2 ? '中' : item.priority === 3 ? '高' : item.priority === 4 ? '紧急' : '普通') }}</span>
              <span v-if="item.dueDate" class="text-xs text-slate-500">{{ formatDate(item.dueDate) }}</span>
            </div>
            <svg class="w-4 h-4 text-slate-300 group-hover:text-primary-400 transition-colors self-center shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7"/></svg>
          </div>
        </div>
        <div v-else class="flex flex-col items-center justify-center py-10">
          <div class="w-16 h-16 rounded-2xl bg-emerald-100 flex items-center justify-center mb-3">
            <svg class="w-8 h-8 text-emerald-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"/></svg>
          </div>
          <p class="text-sm text-slate-400">所有任务已完成</p>
          <router-link to="/todo" class="mt-2 text-xs text-primary-600 hover:text-primary-700">去创建新任务</router-link>
        </div>
        </div>
      </div>

      <!-- 我的提醒 -->
      <div :class="dashboardCardClass" :style="getDashboardCardOrderStyle('reminder')">
        <div class="flex items-center justify-between mb-4 shrink-0">
          <div class="flex items-center gap-2">
            <div class="w-1.5 h-6 bg-primary-500 rounded-full"></div>
            <h3 class="text-base font-semibold text-slate-900">我的提醒</h3>
          </div>
          <router-link to="/reminders/my-tasks" class="group flex items-center gap-1 text-xs text-slate-400 hover:text-primary-600 transition-colors">
            查看全部
            <svg class="w-3.5 h-3.5 transition-transform group-hover:translate-x-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7"/></svg>
          </router-link>
        </div>
        <div :class="dashboardCardBodyClass">
        <div v-if="myReminders.length > 0" class="space-y-2">
          <div v-for="item in myReminders" :key="item.id" class="group relative flex items-start gap-3 p-3.5 rounded-xl cursor-pointer transition-all duration-200 hover:shadow-sm bg-slate-50 border border-slate-200">
            <div class="w-10 h-10 rounded-xl bg-white border border-orange-200 flex items-center justify-center shadow-sm shrink-0 group-hover:shadow-md transition-all">
              <svg class="w-5 h-5 text-orange-600" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9"/></svg>
            </div>
            <div class="flex-1 min-w-0">
              <p class="text-sm font-medium text-slate-800 truncate group-hover:text-orange-700 transition-colors">{{ item.title || '未命名提醒' }}</p>
              <p class="text-xs text-slate-500 mt-0.5 line-clamp-2">{{ item.content || '暂无内容' }}</p>
            </div>
            <button v-if="item.status !== 'CLOSED'" @click.stop="handleProcessReminder(item)" class="px-3.5 py-1.5 bg-primary-600 text-white text-xs font-medium rounded-lg hover:bg-primary-700 shadow-sm hover:shadow-md transition-all whitespace-nowrap self-center shrink-0">处理</button>
            <svg class="w-4 h-4 text-slate-300 group-hover:text-orange-400 transition-colors self-center shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7"/></svg>
          </div>
        </div>
        <div v-else class="flex flex-col items-center justify-center py-10">
          <div class="w-16 h-16 rounded-2xl bg-orange-100 flex items-center justify-center mb-3">
            <svg class="w-8 h-8 text-orange-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9"/></svg>
          </div>
          <p class="text-sm text-slate-400">暂无待处理提醒</p>
          <router-link to="/reminders/my-tasks" class="mt-2 text-xs text-primary-600 hover:text-primary-700">去处理提醒</router-link>
        </div>
        </div>
      </div>

      <!-- 待审批 -->
      <div :class="dashboardCardClass" :style="getDashboardCardOrderStyle('approval')">
        <div class="flex items-center justify-between mb-4 shrink-0">
          <div class="flex items-center gap-2">
            <div class="w-1.5 h-6 bg-primary-500 rounded-full"></div>
            <h3 class="text-base font-semibold text-slate-900">待审批</h3>
            <span v-if="pendingTasks.length > 0" class="px-2 py-0.5 bg-blue-100 text-blue-700 text-xs font-medium rounded-full">{{ pendingTasks.length }}</span>
          </div>
          <router-link to="/approval" class="group flex items-center gap-1 text-xs text-slate-400 hover:text-primary-600 transition-colors">
            查看全部
            <svg class="w-3.5 h-3.5 transition-transform group-hover:translate-x-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7"/></svg>
          </router-link>
        </div>
        <div :class="dashboardCardBodyClass">
        <div v-if="pendingTasks.length > 0" class="space-y-3">
          <div v-for="task in pendingTasks" :key="task.id" class="group relative flex items-center gap-3 p-3.5 rounded-xl bg-slate-50 hover:bg-primary-50 border border-transparent hover:border-primary-100 cursor-pointer transition-all duration-200 hover:shadow-sm" @click="openApprovalDetail(task)">
            <div class="w-10 h-10 rounded-xl bg-white border border-slate-200 flex items-center justify-center shadow-sm shrink-0 group-hover:shadow-md group-hover:border-blue-200 transition-all">
              <svg class="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"/></svg>
            </div>
            <div class="flex-1 min-w-0">
              <p class="text-sm font-medium text-slate-800 truncate group-hover:text-blue-700 transition-colors">{{ task.title }}</p>
              <p class="text-xs text-slate-400 mt-0.5 flex items-center gap-1">
                <svg class="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"/></svg>
                {{ task.applicantName }}<template v-if="task.applicantRole">（<span class="text-slate-500">{{ task.applicantRole }}</span>）</template>
              </p>
            </div>
            <button @click.stop="openApprovalDetail(task)" class="px-3.5 py-1.5 bg-primary-600 text-white text-xs font-medium rounded-lg hover:bg-primary-700 shadow-sm hover:shadow-md transition-all whitespace-nowrap">审批</button>
            <svg class="w-4 h-4 text-slate-300 group-hover:text-blue-400 transition-colors" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7"/></svg>
          </div>
        </div>
        <div v-else class="flex flex-col items-center justify-center py-10">
          <div class="w-16 h-16 rounded-2xl bg-blue-100 flex items-center justify-center mb-3">
            <svg class="w-8 h-8 text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"/></svg>
          </div>
          <p class="text-sm text-slate-400">暂无待审批任务</p>
        </div>
        </div>
      </div>
      <!-- 通知消息 -->
      <div :class="dashboardCardClass" :style="getDashboardCardOrderStyle('message')">
        <div class="flex items-center justify-between mb-4 shrink-0">
          <div class="flex items-center gap-2">
            <div class="w-1.5 h-6 bg-primary-500 rounded-full"></div>
            <h3 class="text-base font-semibold text-slate-900">通知消息</h3>
            <span v-if="messageUnreadCount > 0" class="px-2 py-0.5 bg-violet-100 text-violet-700 text-xs font-medium rounded-full">{{ messageUnreadCount }}</span>
          </div>
          <button @click="markAllRead" class="group flex items-center gap-1 text-xs text-slate-400 hover:text-primary-600 transition-colors">
            <svg class="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 5a2 2 0 012-2h10a2 2 0 012 2v16l-7-3.5L5 21V5z"/>
            </svg>
            全部已读
          </button>
        </div>
        <div :class="dashboardCardBodyClass">
        <div v-if="notifications.length > 0" class="space-y-3">
          <div
            v-for="msg in notifications"
            :key="msg.id"
            class="group relative flex items-start gap-3 p-3.5 rounded-xl cursor-pointer transition-all duration-200 hover:shadow-sm"
            :class="msg.bgClass"
            @click="markAsRead(msg)"
          >
            <!-- 图标 -->
            <div :class="['w-9 h-9 rounded-lg flex items-center justify-center shrink-0', msg.iconBg]">
              <component :is="msg.icon" class="w-4.5 h-4.5" />
            </div>

            <!-- 内容 -->
            <div class="flex-1 min-w-0">
              <div class="flex items-center gap-1.5">
                <p class="text-sm font-medium text-slate-800 group-hover:text-primary-700 transition-colors">{{ msg.title }}</p>
                <!-- 紧急程度标签 -->
                <span
                  v-if="msg.priority === 2"
                  class="px-1.5 py-0.5 bg-red-100 text-red-700 text-xs font-bold rounded"
                >紧急</span>
                <span
                  v-else-if="msg.priority === 1"
                  class="px-1.5 py-0.5 bg-orange-100 text-orange-700 text-xs font-medium rounded"
                >重要</span>
              </div>
              <p class="text-xs text-slate-500 mt-0.5 line-clamp-2">{{ msg.desc }}</p>
            </div>

            <!-- 时间和操作 -->
            <div class="flex flex-col items-end gap-1.5 shrink-0">
              <span class="text-xs text-slate-400 whitespace-nowrap">{{ msg.time }}</span>
              <button
                v-if="hasNotificationNavigation(msg)"
                class="flex items-center gap-1 text-xs text-primary-600 hover:text-primary-700 px-1.5 py-0.5 rounded hover:bg-primary-50 transition-colors cursor-pointer"
                @click.stop="handleNavigationClick(msg)"
              >
                <svg class="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"/>
                </svg>
                查看
              </button>
            </div>

            <!-- 未读指示器 -->
            <div v-if="msg.unread" class="absolute top-3 right-3 w-2 h-2 bg-primary-500 rounded-full"></div>
          </div>
        </div>
        <!-- 空状态 -->
        <div v-else class="flex flex-col items-center justify-center py-10">
          <div class="w-16 h-16 rounded-2xl bg-violet-100 flex items-center justify-center mb-3">
            <svg class="w-8 h-8 text-violet-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9"/>
            </svg>
          </div>
          <p class="text-sm text-slate-400">暂无通知消息</p>
        </div>
        </div>
      </div>
    </div>
  </div>

  <!-- 日程详情弹窗 -->
  <ScheduleDetailModal
    :show="showEventDetail"
    :event="detailEvent"
    :loading="detailLoading"
    :show-edit="false"
    :show-cancel="false"
    @close="closeEventDetail"
    @accept="() => respondToInvite(2)"
    @decline="() => respondToInvite(3)"
  />

  <!-- 审批详情弹窗 -->
  <ApprovalDetailModal
    v-model="showApprovalDetail"
    :instance-id="selectedApprovalTask?.instanceId || selectedApprovalTask?.id"
    :detail-data="approvalDetail"
    :show-actions="true"
    :show-comment-editor="true"
    @approve="handleApproveFromModal"
    @reject="handleRejectFromModal"
    @close="closeApprovalDetail"
  />

  <!-- 待办事项详情弹窗 -->
  <TodoDetailModal
    :show="showTodoDetail"
    :item="currentTodoItem"
    @close="closeTodoDetail"
    @complete="onTodoComplete"
  />

  <!-- 今日计划处理弹窗 -->
  <Teleport to="body">
    <Transition name="modal">
      <div v-if="showTodayPlanDialog" class="fixed inset-0 z-[2000] flex items-center justify-center px-4 py-6">
        <div class="absolute inset-0 bg-slate-900/40 backdrop-blur-sm" @click="closeTodayPlanDialog"></div>
        <section class="relative flex max-h-[86vh] w-full max-w-2xl flex-col overflow-hidden rounded-2xl bg-white shadow-2xl shadow-slate-900/20">
          <div class="flex items-start justify-between gap-4 border-b border-slate-200 px-6 py-4">
            <div class="min-w-0">
              <h3 class="truncate text-base font-semibold text-slate-900">{{ currentTodayPlanItem ? todayPlanTitle(currentTodayPlanItem) : '工作计划' }}</h3>
              <p v-if="currentTodayPlanItem && todayPlanDescription(currentTodayPlanItem)" class="mt-1 line-clamp-2 text-sm text-slate-500">{{ todayPlanDescription(currentTodayPlanItem) }}</p>
            </div>
            <button type="button" class="rounded-lg p-2 text-slate-400 transition-colors hover:bg-slate-100 hover:text-slate-600" @click="closeTodayPlanDialog">
              <svg class="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"/></svg>
            </button>
          </div>

          <form class="min-h-0 flex-1 space-y-4 overflow-y-auto px-6 py-5" @submit.prevent="submitTodayPlanResult">
            <section class="rounded-xl border border-slate-200 bg-slate-50/70 px-4 py-3">
              <span class="form-label">处理状态</span>
              <div class="mt-2 grid grid-cols-2 gap-2">
                <label
                  v-for="option in todayPlanResultOptions"
                  :key="option.value"
                  class="flex cursor-pointer items-center justify-center rounded-lg border px-3 py-2 text-sm font-medium transition-colors"
                  :class="todayPlanForm.result === option.value ? 'border-primary-300 bg-primary-50 text-primary-700' : 'border-slate-200 bg-white text-slate-600 hover:border-slate-300'"
                >
                  <input v-model="todayPlanForm.result" class="sr-only" type="radio" :value="option.value">
                  {{ option.label }}
                </label>
              </div>
            </section>

            <section class="overflow-hidden rounded-xl border border-slate-200 bg-white">
              <header class="flex items-center justify-between gap-3 border-b border-slate-100 px-4 py-3">
                <div class="min-w-0">
                  <h4 class="text-sm font-semibold text-slate-900">批注</h4>
                  <p class="mt-0.5 text-xs text-slate-500">查看并同步待办批注</p>
                </div>
                <span class="shrink-0 rounded-full bg-slate-100 px-2 py-0.5 text-xs text-slate-500">{{ todayPlanAnnotationDisplayCount }} 条</span>
              </header>

              <div class="max-h-56 overflow-y-auto px-4 py-3">
                <div v-if="todayPlanAnnotationLoading" class="flex items-center justify-center gap-2 py-8 text-sm text-slate-500">
                  <div class="h-5 w-5 animate-spin rounded-full border-2 border-slate-200 border-t-primary-500"></div>
                  正在加载批注...
                </div>
                <div v-else-if="todayPlanAnnotationError" class="flex flex-col items-center gap-3 py-8 text-center">
                  <p class="text-sm text-slate-500">批注加载失败，请重试。</p>
                  <button type="button" class="btn-secondary" @click="loadTodayPlanAnnotations">重试</button>
                </div>
                <div v-else-if="!todayPlanAnnotations.length" class="rounded-lg border border-dashed border-slate-200 px-4 py-6 text-center text-sm text-slate-400">
                  暂无批注
                </div>
                <div v-else class="space-y-3">
                  <div v-for="annotation in todayPlanAnnotations" :key="annotation.id || annotation.createTime || annotation.content" class="rounded-lg border border-slate-100 bg-slate-50/70 px-3.5 py-3">
                    <div class="flex flex-wrap items-center justify-between gap-2 text-xs text-slate-400">
                      <span class="font-medium text-slate-600">{{ annotation.creatorName || annotation.createByName || annotation.authorName || '未知用户' }}</span>
                      <span>{{ annotation.createTime || annotation.createdAt || '' }}</span>
                    </div>
                    <p class="mt-1.5 whitespace-pre-wrap break-words text-sm leading-6 text-slate-700">{{ annotation.content || annotation.remark || '--' }}</p>
                  </div>
                </div>
              </div>

              <footer class="border-t border-slate-100 bg-slate-50 px-4 py-3">
                <label class="block">
                  <span class="form-label">新增批注</span>
                  <textarea
                    v-model="todayPlanAnnotationContent"
                    class="form-textarea mt-1 w-full"
                    rows="3"
                    maxlength="1000"
                    placeholder="请输入批注内容"
                  ></textarea>
                </label>
                <div class="mt-3 flex justify-end">
                  <button
                    type="button"
                    class="btn-secondary"
                    :disabled="todayPlanAnnotationSubmitting || !todayPlanAnnotationContent.trim()"
                    @click="submitTodayPlanAnnotation"
                  >
                    {{ todayPlanAnnotationSubmitting ? '提交中' : '提交批注' }}
                  </button>
                </div>
              </footer>
            </section>

            <div class="flex justify-end gap-2 border-t border-slate-200 pt-4">
              <button type="button" class="btn-secondary" :disabled="todayPlanSaving" @click="closeTodayPlanDialog">取消</button>
              <button type="submit" class="btn-primary" :disabled="todayPlanSaving">
                {{ todayPlanSaving ? '保存中' : '保存处理结果' }}
              </button>
            </div>
          </form>
        </section>
      </div>
    </Transition>
  </Teleport>
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted, h, reactive } from 'vue'
import { useRouter } from 'vue-router'
import { ElMessage } from 'element-plus'
import { useRouterStore } from '@/stores/router'
import { useUserStore } from '@/stores/user'
import { scheduleApi } from '@/api/schedule'
import { todoApi } from '@/api/todo'
import { approveTask } from '@/api/flow'
import {
  addWorkItemTodoAnnotation,
  getDailyWorkReport,
  getWorkReportAccessSummary,
  listWorkItemTodoAnnotations,
  saveSummaryDraft
} from '@/api/workReport'
import ApprovalDetailModal from '@/components/ApprovalDetailModal.vue'
import TodoDetailModal from '@/components/TodoDetailModal.vue'
import ScheduleDetailModal from '@/components/ScheduleDetailModal.vue'
import DashboardStatisticsSection from './components/DashboardStatisticsSection.vue'
import { useMessageStore } from '@/composables/useMessageStore'
import { useNotificationNavigation } from '@/composables/useNotificationNavigation'
import { useReminderToast } from '@/composables/useReminderToast'
import { useApprovalToast } from '@/composables/useApprovalToast'
import { chooseReminderTaskAction, reminderTaskActionMessage } from '@/utils/reminderTaskProcess'
import {
  createClientRequestId,
  getReportOptimisticVersion,
  normalizeReportTodoSnapshot,
  workReportItemSourceLabel
} from '@/views/work-report/workReportUtils'

const router = useRouter()
const routerStore = useRouterStore()
const userStore = useUserStore()
const { hasNotificationNavigation, navigateNotification } = useNotificationNavigation(router)
const dashboardCardClass = 'bg-white rounded-2xl shadow-sm border border-slate-200/60 p-6 h-[360px] flex flex-col overflow-hidden'
const dashboardCardBodyClass = 'min-h-0 flex-1 overflow-y-auto pr-1 space-y-2'

// 使用共享的消息状态
const {
  unreadCount: messageUnreadCount,
  messages: storeMessages,
  loadUnreadCount,
  loadRecentMessages,
  markAsRead: storeMarkAsRead,
  markAllRead: storeMarkAllRead
} = useMessageStore()

// SVG Icon Components
const PlusIcon = {
  render() {
    return h('svg', { fill: 'none', stroke: 'currentColor', viewBox: '0 0 24 24' }, [
      h('path', { 'stroke-linecap': 'round', 'stroke-linejoin': 'round', 'stroke-width': '2', d: 'M12 4v16m8-8H4' })
    ])
  }
}

const CheckCircleIcon = {
  render() {
    return h('svg', { fill: 'none', stroke: 'currentColor', viewBox: '0 0 24 24' }, [
      h('path', { 'stroke-linecap': 'round', 'stroke-linejoin': 'round', 'stroke-width': '2', d: 'M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z' })
    ])
  }
}

const DocumentIcon = {
  render() {
    return h('svg', { fill: 'none', stroke: 'currentColor', viewBox: '0 0 24 24' }, [
      h('path', { 'stroke-linecap': 'round', 'stroke-linejoin': 'round', 'stroke-width': '2', d: 'M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01' })
    ])
  }
}

const CubeIcon = {
  render() {
    return h('svg', { fill: 'none', stroke: 'currentColor', viewBox: '0 0 24 24' }, [
      h('path', { 'stroke-linecap': 'round', 'stroke-linejoin': 'round', 'stroke-width': '2', d: 'M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4' })
    ])
  }
}

const CalendarIcon = {
  render() {
    return h('svg', { fill: 'none', stroke: 'currentColor', viewBox: '0 0 24 24' }, [
      h('path', { 'stroke-linecap': 'round', 'stroke-linejoin': 'round', 'stroke-width': '2', d: 'M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z' })
    ])
  }
}

const AlertIcon = {
  render() {
    return h('svg', { fill: 'none', stroke: 'currentColor', viewBox: '0 0 24 24' }, [
      h('path', { 'stroke-linecap': 'round', 'stroke-linejoin': 'round', 'stroke-width': '2', d: 'M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z' })
    ])
  }
}

const SuccessIcon = {
  render() {
    return h('svg', { fill: 'none', stroke: 'currentColor', viewBox: '0 0 24 24' }, [
      h('path', { 'stroke-linecap': 'round', 'stroke-linejoin': 'round', 'stroke-width': '2', d: 'M5 13l4 4L19 7' })
    ])
  }
}

// 快捷入口配置
const quickActions = [
  { label: '新建方案', key: 'createProposal', path: '/proposal', permission: 'plan:save', icon: PlusIcon, iconBg: 'bg-primary-50 text-primary-600', badgeBg: 'bg-primary-500 text-white' },
  { label: '审批中心', key: 'approval', path: '/approval', icon: CheckCircleIcon, iconBg: 'bg-primary-50 text-primary-600', badge: 3, badgeBg: 'bg-red-500 text-white' },
  { label: '产品库', key: 'product', path: '/product', icon: CubeIcon, iconBg: 'bg-primary-50 text-primary-600' },
  { label: '日程安排', key: 'schedule', path: '/schedule', icon: CalendarIcon, iconBg: 'bg-primary-50 text-primary-600' }
]

const workReportAccessSummary = ref(defaultWorkReportAccessSummary())
const canOpenTeamWorkReport = computed(() => Boolean(workReportAccessSummary.value?.teamWorkbenchVisible))
const workReportQuickActions = computed(() => {
  const summary = { ...defaultWorkReportAccessSummary(), ...(workReportAccessSummary.value || {}) }
  const actions = []
  if (summary.selfReportVisible !== false) {
    actions.push({ label: '我的日报', key: 'workReportMy', path: '/work-reports/my', staticRoute: true, icon: DocumentIcon, iconBg: 'bg-primary-50 text-primary-600' })
  }
  if (summary.teamWorkbenchVisible) {
    actions.push({ label: '团队日报', key: 'workReportTeam', path: '/work-reports/team', staticRoute: true, icon: CheckCircleIcon, iconBg: 'bg-primary-50 text-primary-600' })
  }
  return actions
})

const visibleQuickActions = computed(() => {
  return [...quickActions, ...workReportQuickActions.value].filter(item => {
    if (item.visible === false) return false
    return (item.staticRoute || hasRoutePermission(item.path)) && (!item.permission || userStore.hasPermission(item.permission))
  }).slice(0, 6)
})

function defaultWorkReportAccessSummary() {
  return {
    selfReportVisible: true,
    teamWorkbenchVisible: false,
    ruleManageVisible: false,
    defaultTeamScope: 'TEAM'
  }
}

const showStatisticsSection = computed(() => {
  return userStore.roleKeys.includes('FA-CITY-MANAGER') || userStore.roleKeys.includes('REGIONAL_PARTNER')
})

function hasRoutePermission(path) {
  if (!path) return true
  return routerStore.dynamicRoutes.some(route => isRouteMatch(route, path))
}

function isRouteMatch(route, targetPath, parentPath = '') {
  const currentPath = getFullRoutePath(parentPath, route.path)
  if (currentPath === targetPath || targetPath.startsWith(`${currentPath}/`)) {
    return true
  }
  return (route.children || []).some(child => isRouteMatch(child, targetPath, currentPath))
}

function getFullRoutePath(parentPath, path = '') {
  if (!path) return parentPath || '/'
  if (path.startsWith('/')) return path
  if (!parentPath || parentPath === '/') return `/${path}`
  return `${parentPath}/${path}`
}

// 处理快捷入口点击
function handleQuickAction(item) {
  if (item.key === 'createProposal') {
    // 跳转到方案管理页面并带上新建弹窗参数
    router.push({ path: '/proposal', query: { create: 'true' } })
  } else if (item.key === 'approval') {
    // 跳转到审批中心
    router.push('/approval')
  } else if (item.key === 'product') {
    // 跳转到产品库
    router.push('/product')
  } else if (item.key === 'schedule') {
    // 跳转到日程安排
    router.push('/schedule')
  } else if (item.key === 'workReportMy') {
    router.push({ path: '/work-reports/my' })
  } else if (item.key === 'workReportTeam') {
    router.push({
      path: '/work-reports/team',
      query: workReportAccessSummary.value?.defaultTeamScope === 'COPIED_TO_ME' ? { scope: 'copied' } : {}
    })
  }
}

// 通知消息 - 基于共享消息状态派生展示数据
const notifications = computed(() => {
  return storeMessages.value.filter(item => !item.isRead).map(item => ({
    id: item.id,
    title: item.title || '',
    desc: item.content,
    time: item.time,
    bgClass: getBgClassByType(item),
    iconBg: getIconBgClass(item),
    icon: getIconByType(item.rawType),
    unread: !item.isRead,
    type: item.rawType,
    priority: item.priority,
    createTime: item.createTime,
    bizType: item.bizType,
    linkType: item.linkType,
    businessId: item.businessId,
    webUrl: item.webUrl,
    webPath: item.webPath,
    webNewWindow: item.webNewWindow,
    extraParams: item.extraParams
  }))
})

// 根据消息类型获取图标
function getIconByType(type) {
  switch (type) {
    case 'APPROVAL':
    case 'APPROVED':
      return CheckCircleIcon
    case 'COMPLETE':
    case 'FINISH':
      return SuccessIcon
    case 'WARNING':
    case 'URGENT':
      return AlertIcon
    default:
      return DocumentIcon
  }
}

// 根据消息类型和紧急程度获取样式
function getBgClassByType(item) {
  // 根据紧急程度 priority 显示不同背景 (0-普通, 1-重要, 2-紧急)
  if (item.priority === 2) {
    return 'bg-red-50 border border-red-100'
  } else if (item.priority === 1) {
    return 'bg-amber-50 border border-amber-100'
  }

  // 根据类型区分
  switch (item.rawType) {
    case 'APPROVAL':
    case 'APPROVED':
      return 'bg-blue-50 border border-blue-100'
    case 'COMPLETE':
    case 'FINISH':
      return 'bg-emerald-50 border border-emerald-100'
    case 'WARNING':
    case 'URGENT':
      return 'bg-amber-50 border border-amber-100'
    default:
      return 'bg-slate-50 border border-slate-100'
  }
}

// 根据消息类型和紧急程度获取图标样式
function getIconBgClass(item) {
  if (item.priority === 2) {
    return 'bg-red-100 text-red-600'
  } else if (item.priority === 1) {
    return 'bg-orange-100 text-orange-600'
  }

  // 根据类型区分
  switch (item.rawType) {
    case 'APPROVAL':
    case 'APPROVED':
      return 'bg-blue-100 text-blue-600'
    case 'COMPLETE':
    case 'FINISH':
      return 'bg-emerald-100 text-emerald-600'
    case 'WARNING':
    case 'URGENT':
      return 'bg-amber-100 text-amber-600'
    default:
      return 'bg-slate-100 text-slate-600'
  }
}

// 标记单条消息已读（通过共享 store）
async function markAsRead(msg) {
  if (!msg.unread) return
  try {
    await storeMarkAsRead(msg.id)
  } catch (error) {
    console.error('标记已读失败:', error)
  }
}

// 标记全部消息已读（通过共享 store）
async function markAllRead() {
  try {
    await storeMarkAllRead()
    ElMessage.success('已全部标记为已读')
  } catch (error) {
    console.error('标记全部已读失败:', error)
    ElMessage.error('操作失败，请重试')
  }
}

// 判断消息是否有跳转地址
// 处理消息跳转
// 点击消息跳转按钮
async function handleNavigationClick(msg) {
  if (msg.unread) {
    await markAsRead(msg)
  }
  navigateNotification(msg)
}

// 格式化日期
function formatDate(dateStr) {
  if (!dateStr) return ''
  const date = new Date(dateStr)
  const month = date.getMonth() + 1
  const day = date.getDate()
  return `${month}月${day}日`
}

const showTodoDetail = ref(false)
const currentTodoItem = ref(null)

// 打开待办详情弹窗
function openTodoDetail(item) {
  currentTodoItem.value = { ...item }
  showTodoDetail.value = true
}

// 关闭待办详情弹窗
function closeTodoDetail() {
  showTodoDetail.value = false
  currentTodoItem.value = null
}

// 待办完成回调
function onTodoComplete() {
  fetchDashboardTodos()
}

// 首页快速完成待办
async function handleCompleteTodo(item) {
  try {
    await todoApi.complete(item.id)
    await fetchDashboardTodos()
  } catch (error) {
    console.error('完成待办失败:', error)
  }
}

// 今日日程数据
const todaySchedules = ref([])
// 待审批任务数据
const { approvalList, syncHandledApproval } = useApprovalToast()
const pendingTasks = computed(() => approvalList.value)
// 首页待办事项数据
const dashboardTodos = ref([])

// 今日计划数据来自“我的日报”的今日工作项
const todayWorkReport = ref(null)
const todayPlanLoading = ref(false)
const todayPlanSaving = ref(false)
const showTodayPlanDialog = ref(false)
const currentTodayPlanItem = ref(null)
const todayPlanForm = reactive({
  result: 'IN_PROGRESS'
})
const todayPlanAnnotations = ref([])
const todayPlanAnnotationLoading = ref(false)
const todayPlanAnnotationSubmitting = ref(false)
const todayPlanAnnotationError = ref(false)
const todayPlanAnnotationContent = ref('')
let todayPlanAnnotationLoadSeq = 0
const todayPlanResultOptions = [
  { value: 'IN_PROGRESS', label: '进行中' },
  { value: 'COMPLETED', label: '已完成' }
]
const todayPlanItems = computed(() => {
  return (todayWorkReport.value?.workItems || []).filter(item => item?.source !== 'UNPLANNED')
})
const todayPlanPendingItems = computed(() => {
  return todayPlanItems.value.filter(item => item?.result !== 'COMPLETED')
})
const todayPlanHasPlan = computed(() => todayPlanItems.value.length > 0)
const todayPlanSummarySubmitted = computed(() => todayWorkReport.value?.summaryStatus === 'SUBMITTED')
const todayPlanAnnotationDisplayCount = computed(() => todayPlanAnnotations.value.length || todayPlanTodoAnnotationCount(currentTodayPlanItem.value))

// 我的提醒：订阅全局 composable
const { dashboardList, processReminder } = useReminderToast()
const myReminders = computed(() => dashboardList.value)

const dashboardCardOrderMap = computed(() => {
  return [
    { key: 'schedule', count: todaySchedules.value.length, fallbackOrder: 0 },
    { key: 'todayPlan', count: todayPlanPendingItems.value.length, fallbackOrder: 1 },
    { key: 'todo', count: dashboardTodos.value.length, fallbackOrder: 2 },
    { key: 'reminder', count: myReminders.value.length, fallbackOrder: 3 },
    { key: 'approval', count: pendingTasks.value.length, fallbackOrder: 4 },
    { key: 'message', count: messageUnreadCount.value || 0, fallbackOrder: 5 }
  ]
    .sort((a, b) => {
      if (b.count !== a.count) {
        return b.count - a.count
      }
      return a.fallbackOrder - b.fallbackOrder
    })
    .reduce((result, item, index) => {
      result[item.key] = index
      return result
    }, {})
})

function getDashboardCardOrderStyle(cardKey) {
  return {
    order: dashboardCardOrderMap.value[cardKey] ?? 0
  }
}

function todayPlanItemKey(item) {
  return item?.id ?? item?.clientKey ?? `${item?.source || 'PLAN'}-${item?.title || item?.todayGoal || ''}`
}

function todayPlanTitle(item) {
  return item?.title || item?.todayGoal || '未命名计划'
}

function todayPlanDescription(item) {
  return item?.todayGoal || item?.expectedOutcome || item?.actualOutcome || item?.description || ''
}

function todayPlanSourceLabel(item) {
  return workReportItemSourceLabel(item?.source)
}

function todayPlanResultLabel(result) {
  const normalized = normalizeTodayPlanResult(result)
  return todayPlanResultOptions.find(option => option.value === normalized)?.label || '进行中'
}

function normalizeTodayPlanResult(result) {
  return result === 'COMPLETED' ? 'COMPLETED' : 'IN_PROGRESS'
}

function normalizeCollection(value) {
  if (Array.isArray(value)) return value
  if (Array.isArray(value?.records)) return value.records
  if (Array.isArray(value?.list)) return value.list
  if (Array.isArray(value?.rows)) return value.rows
  if (Array.isArray(value?.data)) return value.data
  if (Array.isArray(value?.data?.records)) return value.data.records
  if (Array.isArray(value?.data?.list)) return value.data.list
  return []
}

function todayPlanWorkItemId(item = currentTodayPlanItem.value) {
  return item?.id || item?.workItemId
}

function todayPlanTodoAnnotationCount(item) {
  if (!item) return 0
  const annotations = normalizeCollection(item.annotations || item.todoAnnotations)
  const count = Number(item.annotationCount ?? item.todoAnnotationCount ?? item.currentTodoSnapshot?.annotationCount ?? item.todoSnapshot?.annotationCount ?? annotations.length)
  return Number.isFinite(count) ? count : annotations.length
}

function syncTodayPlanAnnotationCount(workItemId, count) {
  if (!todayWorkReport.value?.workItems?.length || !workItemId) return
  todayWorkReport.value = {
    ...todayWorkReport.value,
    workItems: todayWorkReport.value.workItems.map(item => {
      if (String(todayPlanWorkItemId(item)) !== String(workItemId)) return item
      return {
        ...item,
        annotationCount: count,
        todoAnnotationCount: count,
        currentTodoSnapshot: item.currentTodoSnapshot ? { ...item.currentTodoSnapshot, annotationCount: count } : item.currentTodoSnapshot,
        todoSnapshot: item.todoSnapshot ? { ...item.todoSnapshot, annotationCount: count } : item.todoSnapshot
      }
    })
  }
}

function isSameTodayPlanItem(left, right) {
  if (!left || !right) return false
  if (left.id !== undefined && left.id !== null && right.id !== undefined && right.id !== null) {
    return String(left.id) === String(right.id)
  }
  if (left.clientKey && right.clientKey) {
    return left.clientKey === right.clientKey
  }
  return left === right
}

function goCreateTodayPlan() {
  router.push({ path: '/work-reports/my', query: { reportDate: localDate(), phase: 'plan' } })
}

function goOpenTeamWorkReport() {
  router.push({
    path: '/work-reports/team',
    query: workReportAccessSummary.value?.defaultTeamScope === 'COPIED_TO_ME' ? { scope: 'copied' } : {}
  })
}

function goSubmitTodaySummary() {
  router.push({ path: '/work-reports/my', query: { reportDate: localDate(), phase: 'summary' } })
}

function openTodayPlanDialog(item) {
  currentTodayPlanItem.value = item
  todayPlanForm.result = normalizeTodayPlanResult(item?.result)
  todayPlanAnnotationContent.value = ''
  showTodayPlanDialog.value = true
  loadTodayPlanAnnotations()
}

function closeTodayPlanDialog() {
  if (todayPlanSaving.value || todayPlanAnnotationSubmitting.value) return
  todayPlanAnnotationLoadSeq += 1
  showTodayPlanDialog.value = false
  currentTodayPlanItem.value = null
  todayPlanAnnotations.value = []
  todayPlanAnnotationLoading.value = false
  todayPlanAnnotationError.value = false
  todayPlanAnnotationContent.value = ''
}

async function loadTodayPlanAnnotations() {
  const reportId = todayWorkReport.value?.id
  const workItemId = todayPlanWorkItemId()
  if (!reportId || !workItemId) {
    todayPlanAnnotations.value = []
    return
  }
  const requestSeq = ++todayPlanAnnotationLoadSeq
  todayPlanAnnotationLoading.value = true
  todayPlanAnnotationError.value = false
  try {
    const result = await listWorkItemTodoAnnotations(reportId, workItemId)
    if (requestSeq !== todayPlanAnnotationLoadSeq) return
    todayPlanAnnotations.value = normalizeCollection(result)
    syncTodayPlanAnnotationCount(workItemId, todayPlanAnnotations.value.length)
  } catch (error) {
    if (requestSeq !== todayPlanAnnotationLoadSeq) return
    console.error('加载今日计划批注失败:', error)
    todayPlanAnnotationError.value = true
    ElMessage.error('批注加载失败，请重试')
  } finally {
    if (requestSeq === todayPlanAnnotationLoadSeq) {
      todayPlanAnnotationLoading.value = false
    }
  }
}

async function submitTodayPlanAnnotation() {
  const content = todayPlanAnnotationContent.value.trim()
  const reportId = todayWorkReport.value?.id
  const workItemId = todayPlanWorkItemId()
  if (!content || !reportId || !workItemId) return
  todayPlanAnnotationSubmitting.value = true
  try {
    await addWorkItemTodoAnnotation(reportId, workItemId, { content })
    todayPlanAnnotationContent.value = ''
    ElMessage.success('批注已同步')
    await loadTodayPlanAnnotations()
  } catch (error) {
    console.error('提交今日计划批注失败:', error)
    ElMessage.error('批注提交失败，请重试')
  } finally {
    todayPlanAnnotationSubmitting.value = false
  }
}

async function submitTodayPlanResult() {
  if (!todayWorkReport.value?.id || !currentTodayPlanItem.value) {
    ElMessage.warning('请先创建今日计划')
    return
  }

  const nextResult = normalizeTodayPlanResult(todayPlanForm.result)
  const nextWorkItems = (todayWorkReport.value.workItems || []).map(item => {
    if (!isSameTodayPlanItem(item, currentTodayPlanItem.value)) return item
    return {
      ...item,
      result: nextResult
    }
  })

  todayPlanSaving.value = true
  try {
    const updated = await saveSummaryDraft(todayWorkReport.value.id, buildTodaySummaryDraftPayload(nextWorkItems))
    todayWorkReport.value = updated || { ...todayWorkReport.value, workItems: nextWorkItems }
    todayPlanAnnotationLoadSeq += 1
    showTodayPlanDialog.value = false
    currentTodayPlanItem.value = null
    todayPlanAnnotations.value = []
    todayPlanAnnotationContent.value = ''
    todayPlanAnnotationError.value = false
    ElMessage.success('计划处理结果已保存')
  } catch (error) {
    console.error('保存今日计划处理结果失败:', error)
    ElMessage.error('保存失败，请稍后重试')
  } finally {
    todayPlanSaving.value = false
  }
}

function buildTodaySummaryDraftPayload(workItems) {
  return {
    reportId: todayWorkReport.value.id,
    workItems: workItems.map(normalizeDashboardWorkItemForPayload),
    achievement: todayWorkReport.value.summaryAchievement || '',
    blocker: todayWorkReport.value.summaryBlocker || '',
    assistanceNeeded: todayWorkReport.value.summaryAssistanceNeeded || '',
    supplement: todayWorkReport.value.summarySupplement || '',
    supplementReason: todayWorkReport.value.supplementReason || '',
    attachmentObjectKeys: dashboardAttachmentObjectKeys(todayWorkReport.value),
    expectedVersion: getReportOptimisticVersion(todayWorkReport.value),
    clientRequestId: createClientRequestId()
  }
}

function normalizeDashboardWorkItemForPayload(item) {
  const payload = { ...item }
  delete payload.clientKey
  delete payload.progressPercent
  delete payload.annotations
  delete payload.todoAnnotations

  const todoSnapshot = normalizeReportTodoSnapshot(payload.todoSnapshot)
  if (todoSnapshot) payload.todoSnapshot = todoSnapshot

  const currentTodoSnapshot = normalizeReportTodoSnapshot(payload.currentTodoSnapshot)
  if (currentTodoSnapshot) payload.currentTodoSnapshot = currentTodoSnapshot

  return payload
}

function dashboardAttachmentObjectKeys(report) {
  return (report?.attachments || []).map(attachment => attachment.objectKey).filter(Boolean)
}

function localDate() {
  const now = new Date()
  const year = now.getFullYear()
  const month = String(now.getMonth() + 1).padStart(2, '0')
  const day = String(now.getDate()).padStart(2, '0')
  return `${year}-${month}-${day}`
}

// 日程详情弹窗状态
const showEventDetail = ref(false)
const detailEvent = ref(null)
const detailLoading = ref(false)

// 审批详情弹窗状态
const showApprovalDetail = ref(false)
const selectedApprovalTask = ref(null)
const approvalDetail = ref(null)

// 格式化时间显示
function formatTime(dateTimeStr) {
  if (!dateTimeStr) return ''
  return dateTimeStr.includes('T')
    ? dateTimeStr.split('T')[1]?.substring(0, 5) || ''
    : dateTimeStr.substring(0, 5)
}

// 打开日程详情弹窗
async function openEventDetail(schedule) {
  // 先设置基本数据，确保 isOwner 和 attendeeStatus 立即可用
  detailEvent.value = {
    ...schedule,
    // 确保从 rawData 中获取 isOwner 和 attendeeStatus
    isOwner: schedule.rawData?.isOwner ?? schedule.isOwner,
    attendeeStatus: schedule.rawData?.attendeeStatus ?? schedule.attendeeStatus
  }
  showEventDetail.value = true
  detailLoading.value = true

  try {
    const res = await scheduleApi.getDetail(schedule.id)
    if (res) {
      // 格式化参与人列表
      const attendeeList = res.attendees?.map(a => ({
        id: a.userId,
        userId: a.userId,
        userName: a.userName || '',
        avatar: a.avatar || '',
        role: a.role,
        status: a.status
      })) || []
      const attendeesStr = res.attendees?.map(a => a.userName || '').filter(Boolean).join('、') || ''

      // 解析日期和时间
      let date = ''
      let time = ''
      let endTime = ''
      if (res.startTime) {
        const startDt = res.startTime.includes('T')
          ? res.startTime.split('T')[0]
          : res.startTime.substring(0, 10)
        date = startDt
        time = formatTime(res.startTime)
      }
      if (res.endTime) {
        endTime = formatTime(res.endTime)
      }

      // 合并详情数据，保留 isOwner 和 attendeeStatus（优先使用 API 返回的）
      detailEvent.value = {
        ...detailEvent.value,
        ...res,
        date: date || detailEvent.value.date,
        time: time || detailEvent.value.time,
        endTime,
        attendees: attendeesStr,
        attendeeList,
        // 明确保留邀请状态
        isOwner: res.isOwner !== undefined ? res.isOwner : detailEvent.value.isOwner,
        attendeeStatus: res.attendeeStatus !== undefined ? res.attendeeStatus : detailEvent.value.attendeeStatus
      }
    }
  } catch (error) {
    console.error('获取日程详情失败:', error)
  } finally {
    detailLoading.value = false
  }
}

// 关闭日程详情弹窗
function closeEventDetail() {
  showEventDetail.value = false
  detailEvent.value = null
}

// 响应邀请（接受/拒绝）
async function respondToInvite(status) {
  if (!detailEvent.value) return

  try {
    await scheduleApi.responseInvite(detailEvent.value.id, status)
    ElMessage.success(status === 2 ? '已接受邀请' : '已拒绝邀请')
    closeEventDetail()
    // 刷新今日日程
    fetchTodaySchedules()
  } catch (error) {
    console.error('响应邀请失败:', error)
    ElMessage.error('操作失败，请重试')
  }
}

// 获取背景样式类
function getScheduleBgClass(schedule) {
  const baseClass = 'hover:bg-slate-50'
  switch (schedule.status) {
    case 'pending':
      return `${baseClass} bg-white border border-slate-200`
    case 'in_progress':
      return `${baseClass} bg-blue-50 border border-blue-200`
    case 'completed':
      return `${baseClass} bg-green-50 border border-green-200`
    default:
      return baseClass
  }
}

// 获取状态指示器颜色类
function getScheduleStatusDotClass(schedule) {
  switch (schedule.status) {
    case 'pending':
      return 'bg-amber-500'
    case 'in_progress':
      return 'bg-blue-500'
    case 'completed':
      return 'bg-green-500'
    default:
      return 'bg-slate-400'
  }
}

// 查看日程详情（弹窗方式）
function viewSchedule(schedule) {
  openEventDetail(schedule)
}

// 完成日程
async function completeSchedule(schedule) {
  try {
    await scheduleApi.complete(schedule.id)
    ElMessage.success('日程已完成')
    fetchTodaySchedules()
  } catch (error) {
    console.error('完成日程失败:', error)
    ElMessage.error('操作失败，请重试')
  }
}

// 在首页列表中快速响应邀请
async function respondToInviteOnList(schedule, status) {
  try {
    await scheduleApi.responseInvite(schedule.id, status)
    ElMessage.success(status === 2 ? '已接受邀请' : '已拒绝邀请')
    // 更新列表中的状态
    schedule.status = status === 2 ? 'in_progress' : 'completed'
    schedule.attendeeStatus = status
    // 刷新今日日程列表
    fetchTodaySchedules()
  } catch (error) {
    console.error('响应邀请失败:', error)
    ElMessage.error('操作失败，请重试')
  }
}

// 获取今日日程
async function fetchTodaySchedules() {
  try {
    // request拦截器会在code===0时返回data，所以res就是数组
    const data = await scheduleApi.getToday()
    console.log('[Dashboard] 今日日程数据:', data)
    // 转换API数据格式为前端所需格式
    todaySchedules.value = (data || []).map(item => {
      // 解析时间
      let time = ''
      let startTimeObj = null
      let endTimeObj = null
      if (item.startTime) {
        // 格式: 2026-01-26T10:30:00 或 2026-02-02 10:40:00
        const timeStr = item.startTime.includes('T')
          ? item.startTime.split('T')[1]?.substring(0, 5)
          : item.startTime.split(' ')?.[1]?.substring(0, 5) || ''
        time = timeStr
        // 创建开始时间的Date对象（用于状态判断）
        const datePart = item.startTime.includes('T')
          ? item.startTime.split('T')[0]
          : item.startTime.split(' ')[0]
        if (timeStr && datePart) {
          startTimeObj = new Date(`${datePart}T${timeStr}:00`)
        }
      }
      if (item.endTime) {
        const timeStr = item.endTime.includes('T')
          ? item.endTime.split('T')[1]?.substring(0, 5)
          : item.endTime.split(' ')?.[1]?.substring(0, 5) || ''
        const datePart = item.endTime.includes('T')
          ? item.endTime.split('T')[0]
          : item.endTime.split(' ')[0]
        if (timeStr && datePart) {
          endTimeObj = new Date(`${datePart}T${timeStr}:00`)
        }
      }

      // 根据时间计算状态: pending-未开始, in_progress-进行中, completed-已结束
      const now = new Date()
      let status = 'pending' // 默认未开始
      if (startTimeObj && endTimeObj) {
        if (now >= startTimeObj && now <= endTimeObj) {
          status = 'in_progress' // 进行中
        } else if (now > endTimeObj) {
          status = 'completed' // 已结束
        } else {
          status = 'pending' // 未开始
        }
      } else if (startTimeObj) {
        if (now >= startTimeObj) {
          status = 'in_progress' // 只有开始时间，已开始
        }
      }

      return {
        id: item.id,
        title: item.title || '',
        time,
        endTime: item.endTime ? (item.endTime.includes('T')
          ? item.endTime.split('T')[1]?.substring(0, 5)
          : item.endTime.split(' ')?.[1]?.substring(0, 5) || '') : '',
        location: item.location || '',
        status,
        isOwner: item.isOwner !== undefined ? item.isOwner : true,
        attendeeStatus: item.attendeeStatus,
        // 获取第一个参与人的头像
        attendeeAvatar: item.attendees?.[0]?.avatar || null,
        // 原始数据（用于详情查看）
        rawData: item
      }
    })
    console.log('[Dashboard] 转换后的今日日程:', todaySchedules.value)
  } catch (error) {
    console.error('获取今日日程失败:', error)
    // 使用模拟数据
    todaySchedules.value = []
  }
}

// 获取今日工作计划
async function fetchWorkReportAccessSummary() {
  try {
    workReportAccessSummary.value = { ...defaultWorkReportAccessSummary(), ...(await getWorkReportAccessSummary() || {}) }
  } catch (error) {
    console.error('加载日报入口权限失败:', error)
    workReportAccessSummary.value = defaultWorkReportAccessSummary()
  }
}

async function fetchTodayWorkReportPlans() {
  todayPlanLoading.value = true
  try {
    const data = await getDailyWorkReport(localDate())
    todayWorkReport.value = data || null
  } catch (error) {
    console.error('获取今日工作计划失败:', error)
    todayWorkReport.value = null
  } finally {
    todayPlanLoading.value = false
  }
}

// 处理提醒（延期提醒或标记完成）
async function handleProcessReminder(item) {
  try {
    const result = await chooseReminderTaskAction(item)
    await processReminder(item.id, result.action, item, result)
    ElMessage.success(reminderTaskActionMessage(result.action))
  } catch (e) {
    if (e !== 'cancel' && e !== 'close') {
      ElMessage.error('操作失败')
      console.error('[Dashboard] 处理失败', e)
    }
  }
}

// 获取首页待办事项
async function fetchDashboardTodos() {
  try {
    const data = await todoApi.getList({ pageNum: 1, pageSize: 5, status: 4 })
    dashboardTodos.value = (data?.records || []).map(item => ({
      id: item.id,
      title: item.title,
      description: item.description,
      priority: item.priority,
      priorityDesc: item.priorityDesc,
      dueDate: item.dueDate,
      overdue: item.overdue,
      status: item.status
    }))
    console.log('[Dashboard] 首页待办事项:', dashboardTodos.value)
  } catch (error) {
    console.error('获取待办事项失败:', error)
    dashboardTodos.value = []
  }
}

// 打开审批详情弹窗
function openApprovalDetail(task) {
  selectedApprovalTask.value = task
  approvalDetail.value = null
  showApprovalDetail.value = true
}

// 关闭审批详情弹窗
function closeApprovalDetail() {
  showApprovalDetail.value = false
  selectedApprovalTask.value = null
  approvalDetail.value = null
}

// 通过审批（来自 ApprovalDetailModal）
async function handleApproveFromModal({ comment }) {
  if (!selectedApprovalTask.value) return
  const handledId = selectedApprovalTask.value.id
  try {
    await approveTask({
      instanceId: selectedApprovalTask.value.instanceId || selectedApprovalTask.value.id,
      actionType: 'AGREE',
      remark: comment
    })
    ElMessage.success('审批通过')
    closeApprovalDetail()
    syncHandledApproval(handledId)
  } catch (error) {
    ElMessage.error(error.message || '操作失败')
  }
}

// 驳回审批（来自 ApprovalDetailModal）
async function handleRejectFromModal({ comment }) {
  if (!selectedApprovalTask.value) return
  const handledId = selectedApprovalTask.value.id
  try {
    await approveTask({
      instanceId: selectedApprovalTask.value.instanceId || selectedApprovalTask.value.id,
      actionType: 'REJECT',
      remark: comment
    })
    ElMessage.success('已驳回')
    closeApprovalDetail()
    syncHandledApproval(handledId)
  } catch (error) {
    ElMessage.error(error.message || '操作失败')
  }
}

// 定时刷新间隔（毫秒）
const REFRESH_INTERVAL = 60000 // 1分钟
let refreshTimer = null

// 刷新所有数据
function refreshAllData() {
  fetchTodaySchedules()
  fetchDashboardTodos()
  fetchTodayWorkReportPlans()
  fetchWorkReportAccessSummary()
  loadRecentMessages()
  loadUnreadCount()
}

onMounted(() => {
  // 立即获取数据
  refreshAllData()
  // 设置定时刷新
  refreshTimer = setInterval(refreshAllData, REFRESH_INTERVAL)
})

// 组件卸载时清除定时器
onUnmounted(() => {
  if (refreshTimer) {
    clearInterval(refreshTimer)
    refreshTimer = null
  }
})
</script>

<style scoped>
/* 弹窗动画 */
.modal-enter-active,
.modal-leave-active {
  transition: opacity 0.2s ease;
}

.modal-enter-active .relative,
.modal-leave-active .relative {
  transition: transform 0.3s ease;
}

.modal-enter-from,
.modal-leave-to {
  opacity: 0;
}

.modal-enter-from .relative,
.modal-leave-to .relative {
  transform: scale(0.95);
}
</style>
